import { FormConfig, FormValues, Validator } from '@topdanmark-onlinesolutions/claim-form-types';
import isNotEmpty from './is-not-empty';
import { exactItemsChecked, maxItemsChecked, minItemsChecked } from './item';
import { exactLength, maxLength, minLength } from './length';
import { isNotEqual, isEqual } from './equal';
import { testRegex } from './regex';
import { minYear, maxYear } from './year';
import { radioChecked } from './radiobuttons';
import { validateCpr, validateEmail, validatePhone } from '@flos/react-ui';
import {
    compareDateElements as compareDateToOtherElement,
    compareNumberElement as compareNumberToOtherElement,
    compareStringElement as compareStringToOtherElement
} from './compare';
import {} from '../types/form-values';
import { getElementFromElementId } from '../helpers/getElementFromElementId';
import { getValue } from '../components/values/Get';
import { dropdownChosen } from './dropdowns';
import { validateNumberOfFiles } from './number-of-files';

/**
 * Validate the field and return Validator[] with result
 * @param validators
 * @param value
 * @returns Validator[]
 */

export const getValidationErrors = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string
): string[] => {
    if (!formConfig) {
        return [];
    }

    const element = getElementFromElementId(formConfig, elementId);

    const { validators } = element;

    const value = getValue(formConfig, formValues, elementId);

    const errorMessages: string[] = [];

    validators.forEach((validator: Validator) => {
        const { type, errorMessage } = validator;
        let result = false;

        switch (type) {
            case 'notEmpty':
                result = isNotEmpty(value as string);
                break;

            case 'equal':
                result = isEqual(value as string | number, validator.value);
                break;

            case 'notEqual':
                result = isNotEqual(value as string | number, validator.value);
                break;

            case 'maxLength':
                result = maxLength(value as string, validator.maxLength);
                break;

            case 'minLength':
                result = minLength(value as string, validator.minLength);
                break;

            case 'exactLength':
                result = exactLength(value as string, validator.length);
                break;

            case 'regExp':
                result = testRegex(value as string, validator.regExp);
                break;

            case 'radioChecked':
                result = radioChecked(value as string);
                break;

            case 'dropdownChosen':
                result = dropdownChosen(value as string);
                break;

            case 'exactItemsChecked':
                result = exactItemsChecked(value as string[], validator.items);
                break;

            case 'minItemsChecked':
                result = minItemsChecked(value as string[], validator.min);
                break;

            case 'maxItemsChecked':
                result = maxItemsChecked(value as string[], validator.max);
                break;

            case 'minYear':
                result = minYear(value as string, validator.minYear);
                break;

            case 'maxYear':
                result = maxYear(value as string, validator.maxYear);
                break;

            case 'cprNumber':
                result = validateCpr(value as string);
                break;

            case 'compareDateToOtherElement':
                result = compareDateToOtherElement(
                    formConfig,
                    formValues,
                    value as string,
                    validator.compareElementId,
                    validator.condition
                );
                break;

            case 'compareNumberToOtherElement':
                result = compareNumberToOtherElement(
                    formValues,
                    value as string,
                    validator.compareElementId,
                    validator.condition
                );
                break;
            case 'compareStringToOtherElement':
                result = compareStringToOtherElement(
                    formValues,
                    value as string,
                    validator.compareElementId,
                    validator.condition
                );
                break;
            case 'email':
                result = validateEmail(value);
                break;
            case 'optionalEmail':
                result = value?.length > 0 ? validateEmail(value) : true;

                break;
            case 'phone':
                result = validatePhone(value);
                break;
            case 'optionalPhone':
                result = value?.length > 0 ? validatePhone(value) : true;
                break;
            case 'numberOfFiles':
                result = validateNumberOfFiles(value, validator.minFiles, validator.maxFiles);
                break;
            default:
                throw new Error(`Missing string validator function for type "${type}"`);
        }

        if (!result) {
            errorMessages.push(errorMessage);
        }
    });

    return errorMessages;
};

export const getIsValid = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const errorMessages = getValidationErrors(formConfig, formValues, elementId);

    const isValid = errorMessages.length === 0;

    return isValid;
};
